import type {MessageAttachment} from '@microsoft/agent-host-protocol'
import type {AhpSpaceSelection} from '@github-ui/ahp-session-client/contracts'
import {SpaceIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {use, useCallback} from 'react'
import type {MouseEvent, ReactNode} from 'react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {ownerAvatarPath} from '@github-ui/paths'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {VisuallyHidden} from '@primer/react'
import {ReferenceChip} from '@github-ui/agent-sessions-interface/components/ReferenceChip'
import {
  isPlainReferenceActivation,
  messageAttachmentUrl,
} from '@github-ui/copilot-shared-ui/references/reference-activation'
import {ReferenceChipList} from '@github-ui/agent-sessions-interface/components/ReferenceChipList'
import {UserMessage} from '@github-ui/agent-sessions-interface/components/UserMessage'
import {useSessionContext} from '../../contexts/SessionContext'
import {useSessionLogsContext} from './Context'
import {attachmentToChipProps} from '../../ahp/ahp-attachment-helpers'
import {MessageAttachmentClickContext} from './MessageAttachmentClickContext'
import {UserMessageImages} from './UserMessageImages'
import type {UserMessageImage} from '../../types/user-message-image'
import styles from './UserMessageBubble.module.css'

/**
 * Presentational component for a single user message bubble with avatar,
 * rendered through the shared, pure `UserMessage`:
 * - `images` (inline image previews) render above the text via the `media` slot.
 * - `content` renders as text, or as a pre-rendered node when the session-logs
 *   context supplies a `renderUserMessageContent` transform (e.g. prettified
 *   pasted code).
 * - `attachments` (non-image references) map to `ReferenceChip`s at the top of the bubble.
 *
 * All attachment shaping lives in the AHP consumer (`AhpTurnView` +
 * `ahp-attachment-helpers`); the shared components stay unaware of AHP shapes.
 */
export function UserMessageBubble({
  content,
  images,
  attachments,
  space,
  leading,
  footer,
  avatar: avatarOverride,
  avatarLoading = false,
  avatarBadge,
  showAvatar = true,
  bubbleClassName,
  collapsible = true,
}: {
  content?: string
  images?: UserMessageImage[]
  attachments?: MessageAttachment[]
  space?: AhpSpaceSelection
  leading?: ReactNode
  footer?: ReactNode
  avatar?: {src: string; alt: string}
  avatarLoading?: boolean
  avatarBadge?: {visual: ReactNode; label: string; solidBackground?: boolean}
  showAvatar?: boolean
  bubbleClassName?: string
  collapsible?: boolean
}) {
  const {session} = useSessionContext()
  const {renderUserMessageContent} = useSessionLogsContext()
  const onAttachmentClick = use(MessageAttachmentClickContext)
  const displayName = session.user_login ?? 'user'
  const avatar = avatarOverride ?? {
    src: ownerAvatarPath({owner: session.user_login ?? 'ghost'}),
    alt: `${displayName}'s avatar`,
  }

  const rendered = content != null ? renderUserMessageContent?.(content, {collapsible}) : null
  const media = images && images.length > 0 ? <UserMessageImages images={images} /> : undefined

  const references =
    space || (attachments && attachments.length > 0) ? (
      <ReferenceChipList
        ariaLabel="Attachments"
        items={[
          ...(space
            ? [
                <ReferenceChip
                  key="space"
                  label={`Space: ${space.name}`}
                  wrapLabel
                  href={`/copilot/spaces/${encodeURIComponent(space.owner)}/${space.id}`}
                  leadingVisual={<SpaceIcon size={12} />}
                />,
              ]
            : []),
          ...(attachments?.map((attachment, index) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key -- attachments are a static, order-stable list with no stable id
            <ClickableAttachmentChip key={index} attachment={attachment} onClick={onAttachmentClick} />
          )) ?? []),
        ]}
      />
    ) : undefined

  const renderedAvatar = avatarLoading ? (
    <span aria-hidden="true" data-testid="user-message-avatar-loading">
      <LoadingSkeleton variant="elliptical" height="28px" width="28px" />
    </span>
  ) : (
    <GitHubAvatar src={avatar.src} alt={avatar.alt} size={28} />
  )

  return (
    <UserMessage
      text={rendered ?? content ?? ''}
      avatar={
        !showAvatar ? undefined : avatarBadge ? (
          <span className={styles.AvatarWithBadge}>
            {renderedAvatar}
            <span
              className={clsx(styles.AvatarBadge, avatarBadge.solidBackground && styles.solidBackground)}
              data-testid="user-message-avatar-badge"
            >
              {avatarBadge.visual}
              <VisuallyHidden>via {avatarBadge.label}</VisuallyHidden>
            </span>
          </span>
        ) : (
          renderedAvatar
        )
      }
      media={media}
      references={references}
      leading={leading}
      footer={footer}
      bubbleClassName={bubbleClassName}
    />
  )
}

export function ClickableAttachmentChip({
  attachment,
  onClick,
}: {
  attachment: MessageAttachment
  onClick: ((attachment: MessageAttachment, event: React.MouseEvent) => void) | null
}) {
  const {label, icon: Icon, color} = attachmentToChipProps(attachment)
  const href = messageAttachmentUrl(attachment)
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!isPlainReferenceActivation(event)) return
      onClick?.(attachment, event)
    },
    [attachment, onClick],
  )
  return (
    <ReferenceChip
      label={label}
      leadingVisual={<Icon size={12} className={color ? styles[color] : undefined} />}
      href={href}
      onClick={onClick ? handleClick : undefined}
    />
  )
}
